import React, { useContext, useEffect, useRef, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import Pagination from "react-js-pagination";
import { NoteState } from "../../context/notes/NoteState";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

const SLOT_SESSION_MIN = 30;
const SLOT_CUSHION_MIN = 10;
const SLOT_START_HOUR = 12;
const SLOT_END_HOUR = 20;

const TODAY = () => moment().format("YYYY-MM-DD");

const emptyForm = (branchId, physioId = "") => ({
  appointment_source: "insider",
  appointment_type: "consultation",
  conduct_status: "pending",
  client_id: "",
  client_name: "",
  contact: "",
  gender: "",
  age: "",
  appointment_date: TODAY(),
  slot_start: "",
  notes: "",
  is_vitality_member: false,
  is_vitality_eligible: false,
  branch_id: branchId,
  physio_id: physioId,
});

const formatSlotLabel = (value) => {
  if (!value) return "—";
  const start = moment(value, ["HH:mm:ss", "HH:mm"]);
  const end = start.clone().add(SLOT_SESSION_MIN, "minutes");
  return `${start.format("hh:mm A")} - ${end.format("hh:mm A")}`;
};

const normalizeAgeValue = (value) => {
  const age = Number(value);
  if (!Number.isFinite(age)) return "";
  if (age < 1 || age > 120) return "";
  return String(Math.trunc(age));
};

const localSlotOptions = () => {
  const slots = [];
  let cursor = moment({ hour: SLOT_START_HOUR, minute: 0, second: 0 });
  const closing = moment({ hour: SLOT_END_HOUR, minute: 0, second: 0 });

  while (cursor.clone().add(SLOT_SESSION_MIN, "minutes").isSameOrBefore(closing)) {
    const value = cursor.format("HH:mm");
    slots.push({
      value,
      label: formatSlotLabel(value),
      booked_count: 0,
    });
    cursor = cursor.add(SLOT_SESSION_MIN + SLOT_CUSHION_MIN, "minutes");
  }

  return slots;
};

export default function PhysioAppointments() {
  const { state } = useContext(NoteState);
  const { branch_id, role, UId } = state;

  const isPhysioUser = role === "15";
  const defaultPhysioId = isPhysioUser ? UId : "";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [physios, setPhysios] = useState([]);
  const [clients, setClients] = useState([]);
  const [slotOptions, setSlotOptions] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm(branch_id, defaultPhysioId));

  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterConduct, setFilterConduct] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(TODAY());
  const [filterEndDate, setFilterEndDate] = useState(TODAY());
  const [limit, setLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const [clientSearch, setClientSearch] = useState("");
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  useEffect(() => {
    fetchAppointments(1, limit);
    fetchClients();
    fetchPhysios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch_id]);

  useEffect(() => {
    const handler = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!formData.appointment_date || !branch_id) {
      setSlotOptions([]);
      return;
    }
    fetchSlotOptions(formData.appointment_date, editMode ? selected?.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.appointment_date, formData.physio_id]);

  const fetchAppointments = async (page = 1, lim = limit, override = {}) => {
    if (!branch_id) return;
    setLoading(true);
    try {
      const params = {
        branch_id,
        limit: lim,
        page,
        physio_id: isPhysioUser ? UId : override.physio_id || undefined,
        search: (override.search ?? search) || undefined,
        source: (override.source ?? filterSource) || undefined,
        appointment_type: (override.appointment_type ?? filterType) || undefined,
        conduct_status: (override.conduct_status ?? filterConduct) || undefined,
        start_date: (override.start_date ?? filterStartDate) || undefined,
        end_date: (override.end_date ?? filterEndDate) || undefined,
      };
      const res = await UserServices.getPhysioAppointments(params);
      if (res.status === 200 && res.data.status) {
        setAppointments(res.data.data || []);
        if (res.data.pagination) {
          setCurrentPage(res.data.pagination.current_page || 1);
          setTotalResults(res.data.pagination.total_record || 0);
        } else {
          setCurrentPage(1);
          setTotalResults((res.data.data || []).length);
        }
      }
    } catch (err) {
      console.log(err);
      setAppointments([]);
      setTotalResults(0);
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const res = await UserServices.getClientsName(branch_id);
      if (res.status === 200) {
        const rows = (res.data.data || []).map((c) => ({
          id: c.id,
          name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
          phone: c.phone,
        }));
        setClients(rows);
        setFilteredClients(rows);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPhysios = async () => {
    try {
      const res = await UserServices.getPhysioUsers(branch_id);
      if (res.status === 200 && res.data.status) {
        setPhysios(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchSlotOptions = async (appointmentDate, excludeId = null) => {
    try {
      const res = await UserServices.getPhysioAppointmentSlotOptions(
        appointmentDate,
        branch_id,
        isPhysioUser ? UId : formData.physio_id || null,
        excludeId
      );
      if (res.status === 200 && res.data.status) {
        const normalized = (res.data.data || []).map((slot) => {
          const value = String(slot.value || "").substring(0, 5);
          return {
            value,
            label: formatSlotLabel(value),
            booked_count: Number(slot.booked_count || 0),
          };
        });
        setSlotOptions(normalized.length > 0 ? normalized : localSlotOptions());
        return;
      }
    } catch (err) {
      console.log(err);
    }
    setSlotOptions(localSlotOptions());
  };

  const openAdd = () => {
    setEditMode(false);
    setSelected(null);
    setFormData(emptyForm(branch_id, defaultPhysioId));
    setClientSearch("");
    setShowClientDropdown(false);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelected(row);
    const source = row.appointment_source || (row.client_id ? "insider" : "outsider");
    setFormData({
      appointment_source: source,
      appointment_type: row.appointment_type || "consultation",
      conduct_status: row.conduct_status || "pending",
      client_id: row.client_id || "",
      client_name: row.client_name || "",
      contact: row.contact || "",
      gender: row.gender || "",
      age: row.age || "",
      appointment_date: row.appointment_date ? moment(row.appointment_date).format("YYYY-MM-DD") : "",
      slot_start: row.slot_start ? String(row.slot_start).substring(0, 5) : "",
      notes: row.notes || "",
      is_vitality_member: !!row.is_vitality_member,
      is_vitality_eligible: !!row.is_vitality_eligible,
      branch_id: row.branch_id || branch_id,
      physio_id: row.physio_id || defaultPhysioId,
    });
    setClientSearch(row.client_name || "");
    setShowClientDropdown(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelected(null);
    setFormData(emptyForm(branch_id, defaultPhysioId));
    setClientSearch("");
    setShowClientDropdown(false);
    setSaving(false);
  };

  const handleSourceChange = (source) => {
    setFormData((prev) => ({
      ...prev,
      appointment_source: source,
      client_id: source === "outsider" ? "" : prev.client_id,
      is_vitality_member: source === "outsider" ? false : prev.is_vitality_member,
      is_vitality_eligible: source === "outsider" ? false : prev.is_vitality_eligible,
    }));
    if (source === "outsider") {
      setClientSearch("");
      setShowClientDropdown(false);
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientSearch = (e) => {
    const v = e.target.value;
    setClientSearch(v);
    setShowClientDropdown(true);
    setFilteredClients(
      !v.trim()
        ? clients
        : clients.filter(
            (c) =>
              c.name.toLowerCase().includes(v.toLowerCase()) ||
              (c.phone && c.phone.includes(v))
          )
    );
  };

  const handleClientSelect = async (client) => {
    setClientSearch(client.name);
    setShowClientDropdown(false);
    setFormData((prev) => ({
      ...prev,
      appointment_source: "insider",
      client_id: client.id,
      client_name: client.name,
    }));
    try {
      const res = await UserServices.getPhysioAppointmentClientDetails(client.id);
      if (res.status === 200 && res.data.status) {
        const d = res.data.data || {};
        setFormData((prev) => ({
          ...prev,
          client_id: d.id || client.id,
          client_name: d.client_name || client.name,
          contact: d.contact || "",
          gender: d.gender || "",
          age: normalizeAgeValue(d.age),
          is_vitality_member: !!d.is_vitality_member,
          is_vitality_eligible: !!d.is_vitality_eligible,
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!formData.appointment_date || !formData.slot_start) {
      alert("Please select appointment date and slot.");
      return;
    }
    if (formData.appointment_source === "insider" && !formData.client_id) {
      alert("Please select an insider client.");
      return;
    }
    if (formData.appointment_source === "outsider" && !formData.client_name.trim()) {
      alert("Please enter outsider client name.");
      return;
    }

    setSaving(true);
    const payload = {
      ...formData,
      slot_start: formData.slot_start,
      age: normalizeAgeValue(formData.age) || null,
      client_id: formData.appointment_source === "insider" ? formData.client_id : null,
      physio_id: isPhysioUser ? UId : formData.physio_id || null,
    };

    try {
      if (editMode && selected) {
        await UserServices.updatePhysioAppointment(selected.id, payload);
      } else {
        await UserServices.createPhysioAppointment(payload);
      }
      closeModal();
      fetchAppointments(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert(err?.response?.data?.message || "Failed to save appointment");
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await UserServices.deletePhysioAppointment(id);
      fetchAppointments(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert("Failed to delete appointment");
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await UserServices.changePhysioAppointmentStatus(id, status);
      fetchAppointments(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert("Failed to change status");
    }
  };

  const handleConduct = async (id, conductStatus) => {
    try {
      await UserServices.updatePhysioAppointment(id, {
        conduct_status: conductStatus,
      });
      fetchAppointments(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert(err?.response?.data?.message || "Failed to update conduct status");
    }
  };

  const handleSearch = () => {
    fetchAppointments(1, limit, {});
  };

  const clearFilters = () => {
    const today = TODAY();
    setSearch("");
    setFilterSource("");
    setFilterType("");
    setFilterConduct("");
    setFilterStartDate(today);
    setFilterEndDate(today);
    fetchAppointments(1, limit, {
      search: "",
      source: "",
      appointment_type: "",
      conduct_status: "",
      start_date: today,
      end_date: today,
    });
  };

  const setDayFilter = (dateValue) => {
    setFilterStartDate(dateValue);
    setFilterEndDate(dateValue);
    fetchAppointments(1, limit, {
      start_date: dateValue,
      end_date: dateValue,
    });
  };

  const groupedAppointments = appointments.reduce((acc, row) => {
    const key = row.appointment_date
      ? moment(row.appointment_date).format("YYYY-MM-DD")
      : "unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  const conductBadge = (status) => {
    if (status === "conducted") return "badge bg-success";
    if (status === "not_conducted") return "badge bg-danger";
    return "badge bg-secondary";
  };

  const handleExcel = async () => {
    try {
      const res = await UserServices.exportPhysioAppointments({
        branch_id,
        physio_id: isPhysioUser ? UId : undefined,
        search: search || undefined,
        source: filterSource || undefined,
        start_date: filterStartDate || undefined,
        end_date: filterEndDate || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Physio_Appointments_${moment().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      alert("Failed to export Excel");
    }
  };

  const handlePdf = () => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(16);
      doc.text("Physio Appointments", 14, 16);
      doc.setFontSize(9);
      doc.text(`Generated: ${moment().format("DD MMM YYYY")}`, 14, 23);
      doc.autoTable({
        startY: 28,
        head: [[
          "S#",
          "Date",
          "Client",
          "Source",
          "Contact",
          "Gender/Age",
          "Slot",
          "Vitality",
          "Notes",
        ]],
        body: appointments.map((a, i) => [
          (currentPage - 1) * limit + i + 1,
          a.appointment_date ? moment(a.appointment_date).format("DD/MM/YY") : "",
          a.client_name || "N/A",
          a.appointment_source || "",
          a.contact || "",
          `${a.gender || ""}${a.age ? ` / ${a.age}` : ""}`,
          formatSlotLabel(a.slot_start),
          `${a.is_vitality_member ? "Member" : "Not"} | ${a.is_vitality_eligible ? "Eligible" : "Not Eligible"}`,
          a.notes || "",
        ]),
        headStyles: { fillColor: [69, 39, 160], textColor: 255 },
        styles: { fontSize: 8 },
      });
      doc.save(`Physio_Appointments_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.log(err);
      alert("Failed to generate PDF");
    }
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="page-heading d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">
            <i className="bi bi-calendar2-check me-2"></i>
            Physio Appointments
          </h3>
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="bi bi-plus-circle me-1"></i>Add Appointment
          </button>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-md-2">
                <label className="form-label">Day / From</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All types</option>
                  <option value="consultation">Consultation</option>
                  <option value="session">Session</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Conduct</label>
                <select
                  className="form-select"
                  value={filterConduct}
                  onChange={(e) => setFilterConduct(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="conducted">Conducted</option>
                  <option value="not_conducted">Not conducted</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Source</label>
                <select
                  className="form-select"
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="insider">Insider</option>
                  <option value="outsider">Outsider</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Search</label>
                <input
                  className="form-control"
                  placeholder="Client / phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-12 d-flex flex-wrap gap-2 mt-1">
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setDayFilter(TODAY())}>
                  Today
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setDayFilter(moment().subtract(1, "day").format("YYYY-MM-DD"))}
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setDayFilter(moment().add(1, "day").format("YYYY-MM-DD"))}
                >
                  Tomorrow
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleSearch}>Apply</button>
                <button className="btn btn-secondary btn-sm" onClick={clearFilters}>Today reset</button>
                <button className="btn btn-success btn-sm" onClick={handleExcel}>Excel</button>
                <button className="btn btn-danger btn-sm" onClick={handlePdf}>PDF</button>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center text-muted py-4">No appointments found.</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>S#</th>
                        <th>Date</th>
                        <th>Client</th>
                        <th>Type</th>
                        <th>Conduct</th>
                        <th>Source</th>
                        <th>Contact</th>
                        <th>Slot</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(groupedAppointments).map((dayKey) => (
                        <React.Fragment key={dayKey}>
                          <tr className="table-secondary">
                            <td colSpan={10} className="fw-semibold">
                              {dayKey === "unknown"
                                ? "No date"
                                : moment(dayKey).format("dddd, DD MMM YYYY")}
                              {" · "}
                              {groupedAppointments[dayKey].length} appointment(s)
                            </td>
                          </tr>
                          {groupedAppointments[dayKey].map((row, i) => (
                            <tr key={row.id}>
                              <td>{i + 1}</td>
                              <td>
                                {row.appointment_date
                                  ? moment(row.appointment_date).format("DD/MM/YY")
                                  : "—"}
                              </td>
                              <td>{row.client_name || "—"}</td>
                              <td className="text-capitalize">
                                {row.appointment_type || "consultation"}
                              </td>
                              <td>
                                <span className={conductBadge(row.conduct_status)}>
                                  {(row.conduct_status || "pending").replace("_", " ")}
                                </span>
                              </td>
                              <td className="text-capitalize">
                                {row.appointment_source || "—"}
                              </td>
                              <td>{row.contact || "—"}</td>
                              <td>{formatSlotLabel(row.slot_start)}</td>
                              <td style={{ maxWidth: 180 }}>{row.notes || "—"}</td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    title="Mark conducted"
                                    onClick={() => handleConduct(row.id, "conducted")}
                                  >
                                    Done
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    title="Mark not conducted"
                                    onClick={() => handleConduct(row.id, "not_conducted")}
                                  >
                                    Missed
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => openEdit(row)}
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDelete(row.id)}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalResults > limit && (
                  <div className="d-flex justify-content-center">
                    <Pagination
                      activePage={currentPage}
                      itemsCountPerPage={limit}
                      totalItemsCount={totalResults}
                      pageRangeDisplayed={5}
                      onChange={(p) => {
                        setCurrentPage(p);
                        fetchAppointments(p, limit);
                      }}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <Modal show={showModal} onHide={closeModal} size="lg" backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? "Edit Appointment" : "Add Appointment"}</Modal.Title>
          </Modal.Header>
          <form onSubmit={submit}>
            <Modal.Body>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Inside / Outside</label>
                  <div className="d-flex gap-3 mt-1">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={formData.appointment_source === "insider"}
                        onChange={() => handleSourceChange("insider")}
                      />
                      <label className="form-check-label">Insider</label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={formData.appointment_source === "outsider"}
                        onChange={() => handleSourceChange("outsider")}
                      />
                      <label className="form-check-label">Outsider</label>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Appointment type</label>
                  <select
                    className="form-select"
                    name="appointment_type"
                    value={formData.appointment_type || "consultation"}
                    onChange={handleInput}
                    required
                  >
                    <option value="consultation">Consultation</option>
                    <option value="session">Session</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Conduct status</label>
                  <select
                    className="form-select"
                    name="conduct_status"
                    value={formData.conduct_status || "pending"}
                    onChange={handleInput}
                  >
                    <option value="pending">Pending</option>
                    <option value="conducted">Conducted</option>
                    <option value="not_conducted">Not conducted</option>
                  </select>
                </div>

                {!isPhysioUser ? (
                  <div className="col-md-6">
                    <label className="form-label">Physiotherapist</label>
                    <select
                      className="form-select"
                      name="physio_id"
                      value={formData.physio_id}
                      onChange={handleInput}
                      required
                    >
                      <option value="">Select physiotherapist</option>
                      {physios.map((p) => (
                        <option key={p.id} value={p.id}>
                          {`${p.first_name || ""} ${p.last_name || ""}`.trim()}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                {formData.appointment_source === "insider" ? (
                  <div className="col-md-12">
                    <label className="form-label">Select Client</label>
                    <div className="position-relative" ref={clientDropdownRef}>
                      <input
                        className="form-control"
                        placeholder="Search client by name/phone"
                        value={clientSearch}
                        onChange={handleClientSearch}
                        onFocus={() => {
                          setShowClientDropdown(true);
                          setFilteredClients(clients);
                        }}
                      />
                      {showClientDropdown && filteredClients.length > 0 ? (
                        <div
                          className="position-absolute w-100 bg-white border rounded shadow-sm"
                          style={{ maxHeight: 220, overflowY: "auto", zIndex: 1050 }}
                        >
                          {filteredClients.slice(0, 50).map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="btn btn-link text-start w-100 border-bottom text-decoration-none"
                              onClick={() => handleClientSelect(c)}
                            >
                              <div className="fw-semibold text-dark">{c.name}</div>
                              <small className="text-muted">{c.phone || ""}</small>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <div className="col-md-6">
                  <label className="form-label">Client Name</label>
                  <input
                    className="form-control"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInput}
                    required
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Contact</label>
                  <input
                    className="form-control"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInput}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Gender</label>
                  <input
                    className="form-control"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInput}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Age</label>
                  <input
                    type="number"
                    className="form-control"
                    name="age"
                    value={formData.age}
                    onChange={handleInput}
                    min={1}
                    max={120}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Appointment Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="appointment_date"
                    value={formData.appointment_date}
                    onChange={handleInput}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Time Slot</label>
                  <select
                    className="form-select"
                    name="slot_start"
                    value={formData.slot_start}
                    onChange={handleInput}
                    required
                  >
                    <option value="">Select slot</option>
                    {slotOptions.map((slot) => (
                      <option key={slot.value} value={slot.value}>
                        {slot.label} ({slot.booked_count || 0} booked)
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    Working hours 12:00 PM - 8:00 PM, 30 min session + 10 min cushion.
                  </small>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Vitality Member</label>
                  <input
                    className="form-control"
                    value={formData.is_vitality_member ? "Yes" : "No"}
                    readOnly
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Vitality Eligible</label>
                  <input
                    className="form-control"
                    value={formData.is_vitality_eligible ? "Yes" : "No"}
                    readOnly
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInput}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : editMode ? "Update" : "Save"}
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </div>
  );
}
